/**
 * Pilotage-plan geometry — the pure helpers the map layer and the tap handling
 * lean on. Kept free of any store or map so it's easy to test: per-leg times, the
 * anti-overlap placement of the leg-time labels, which side of a line a point sits
 * on (for a clearing bearing's safe side or a contour's shallow side), and pulling
 * the stretch of a contour between two tapped points (for "follow as route").
 */
import { rhumbDistanceNm, rhumbBearingDeg, rhumbDestination } from '@/nav';
import type { LonLat } from './plannerModel';

/** Minutes to run a leg of `distNm` at `speedKn` (0 when speed is nil). */
export function legMinutes(distNm: number, speedKn: number): number {
  return speedKn > 0 ? (distNm / speedKn) * 60 : 0;
}

/** A compact time-to-go label, e.g. "8m", "1h05". */
export function formatLegTime(min: number): string {
  const m = Math.round(min);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`;
}

/** Midpoint of a short leg (plane average, fine at pilotage scale). */
export function midpoint(a: LonLat, b: LonLat): LonLat {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

export interface LegLabel {
  /** Where to draw the label, [lon, lat]. */
  position: LonLat;
  /** The text, e.g. "8m". */
  text: string;
  /** The leg index (from waypoint i to i+1). */
  legIndex: number;
}

/**
 * Place a time-to-go label on each leg. Long legs get the label on the leg's
 * midpoint. Short legs (below `shortLegNm`) would collide, so their labels are
 * pushed off to the side by `offsetNm`, alternating side leg by leg, so a run of
 * short legs fans its labels apart instead of stacking them on top of each other.
 */
export function legLabels(
  positions: LonLat[],
  speedKn: number,
  opts: { shortLegNm?: number; offsetNm?: number } = {},
): LegLabel[] {
  const shortLegNm = opts.shortLegNm ?? 0.3;
  const offsetNm = opts.offsetNm ?? 0.08;
  const out: LegLabel[] = [];
  let shortRun = 0;
  for (let i = 0; i + 1 < positions.length; i++) {
    const a = positions[i];
    const b = positions[i + 1];
    const dist = rhumbDistanceNm(a, b);
    const mid = midpoint(a, b);
    const text = formatLegTime(legMinutes(dist, speedKn));
    if (dist < shortLegNm) {
      // Push perpendicular to the leg, alternating side across the short run.
      const brg = rhumbBearingDeg(a, b);
      const side = shortRun % 2 === 0 ? 90 : -90;
      const pos = rhumbDestination(mid, (brg + side + 360) % 360, offsetNm * (1 + Math.floor(shortRun / 2)));
      out.push({ position: pos, text, legIndex: i });
      shortRun += 1;
    } else {
      out.push({ position: mid, text, legIndex: i });
      shortRun = 0;
    }
  }
  return out;
}

/**
 * Which side of the directed line a→b a point p lies on, looking from a towards b.
 * 'left' / 'right' / 'on' (within a whisker). Used for a clearing bearing's safe
 * side and a clearing depth's shallow side.
 */
export function sideOfLine(a: LonLat, b: LonLat, p: LonLat): 'left' | 'right' | 'on' {
  // Scale longitude by cos(lat) so the cross product is in a locally-square frame.
  const k = Math.cos((a[1] * Math.PI) / 180);
  const abx = (b[0] - a[0]) * k;
  const aby = b[1] - a[1];
  const apx = (p[0] - a[0]) * k;
  const apy = p[1] - a[1];
  const cross = abx * apy - aby * apx;
  const eps = 1e-9;
  if (cross > eps) return 'left';
  if (cross < -eps) return 'right';
  return 'on';
}

/** Index of the vertex in `coords` nearest to `p` (squared plane distance). */
export function nearestVertexIndex(coords: LonLat[], p: LonLat): number {
  let best = -1;
  let bestD = Infinity;
  const k = Math.cos((p[1] * Math.PI) / 180);
  for (let i = 0; i < coords.length; i++) {
    const dx = (coords[i][0] - p[0]) * k;
    const dy = coords[i][1] - p[1];
    const d = dx * dx + dy * dy;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/**
 * The stretch of a contour (`coords`) between the vertices nearest to `p1` and
 * `p2`, returned in path order (always from the lower index to the higher), for
 * "follow this contour as route". Returns at least the two endpoints.
 */
export function contourSubPath(coords: LonLat[], p1: LonLat, p2: LonLat): LonLat[] {
  if (coords.length === 0) return [];
  let i = nearestVertexIndex(coords, p1);
  let j = nearestVertexIndex(coords, p2);
  if (i > j) [i, j] = [j, i];
  return coords.slice(i, j + 1).map((c) => [c[0], c[1]] as LonLat);
}
